<template>
    <v-form ref="form" v-model="valid" data-cy="guest-category-form">
        <v-row dense class="pb-2">
            <v-col cols="12" md="4">
                <v-row>
                    <v-switch
                        v-model="schedule.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="schedule.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </v-row>
                <v-select
                    v-model="schedule.categoryId"
                    :items="useCharterServiceCategories"
                    :label="`${$t('flight_number')}*`"
                    item-title="name"
                    item-value="id"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    data-cy="category"
                    :rules="[requiredRule('flight_number')]"
                />
                <div class="text-subtitle-1 pb-1">{{ $t('flights.departure_time_local') }}</div>
                <v-text-field
                    v-model="schedule.startTime"
                    label="(HH:mm)"
                    :rules="[timeFormatRule()]"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="start-time"
                    maxlength="5"
                    max-width="100"
                    @update:model-value="e => formatTime(e, 'startTime')"
                />
                <div class="text-subtitle-1 pb-1">{{ $t('flights.arrival_time_local') }}</div>
                <v-text-field
                    v-model="schedule.endDate"
                    label="(HH:mm)"
                    :rules="[timeFormatRule()]"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="end-time"
                    maxlength="5"
                    max-width="100"
                    @update:model-value="e => formatTime(e, 'endDate')"
                />
                <div class="text-subtitle-1 pb-1">{{ $t('duration') }}*</div>
                <v-text-field
                    v-model="schedule.duration"
                    label="(HH:mm)"
                    :rules="[requiredRule('duration'), timeFormatRule()]"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="duration"
                    maxlength="5"
                    max-width="100"
                    @update:model-value="e => formatTime(e, 'duration')"
                />
            </v-col>
        </v-row>

        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule, timeFormatRule} from '~/composables/rules'
    import type {CharterScheduleType} from '~/utils/types/account/products/types'
    import {initCharterSchedule} from '~/utils/types/account/products/helpers'
    import {useCharterServiceCategories} from '~/composables/form-helpers'

    const props = defineProps<{
        editableSchedule?: any | null
        contractId: number
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const schedule = ref<CharterScheduleType>(initCharterSchedule())

    onMounted(async () => {
        if (props.editableSchedule) schedule.value = clone(props.editableSchedule)
        await supplierStore.loadCharterServiceCategories()
    })

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(schedule.value)
            clean(data)
            delete data.name
            delete data.hidden

            if (props.editableSchedule) {
                delete data.categoryId
                await app.$api.ownCharterServices.put({
                    id: props.editableSchedule.id,
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownCharterServices.post({
                    contractId: props.contractId,
                    ...data,
                })
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        schedule.value[key] = formattedValue
    }
</script>
